import type { CustomColumnDto, TrackerLayoutDto } from '@inference.engine/workspace-api-client/web';
import { i18n } from '@/shared/i18n';
import type { BuiltinTrackerTemplate } from './types';

/**
 * Product/default tracker templates — shipped in code (not the DB). Each
 * `layout` is a real `TrackerLayoutDto` snapshot: `customColumns` use the
 * persisted shape where a status column's options live under
 * `config.options` (NOT the FE-only `statusOptions`), matching what the board
 * loader reads. Applying one yields a fully-configured, empty board.
 */

type StatusOption = { id: string; label: string; color: string };

/** Build a status custom column with its options under `config.options`. */
function statusColumn(id: string, label: string, options: StatusOption[]): CustomColumnDto {
  return { id, label, type: 'status', config: { options } };
}

function column(id: string, label: string, type: CustomColumnDto['type']): CustomColumnDto {
  return { id, label, type };
}

/* eslint-disable i18next/no-literal-string -- column ids, status option ids, hex colours and view wire values */

/**
 * Palettes a template's status columns ship with. Factories, not consts: the
 * labels are seeded into the board's own (persisted, editable) layout, so they
 * must resolve in the language active when the template is applied rather than
 * whichever one was active at import.
 */
const workflowStatus = (): StatusOption[] => [
  {
    id: 'not_started',
    label: i18n.t('trackers:trackerTemplate.builtinTemplates.notStarted'),
    color: '#94a3b8',
  },
  {
    id: 'in_progress',
    label: i18n.t('trackers:trackerTemplate.builtinTemplates.inProgress'),
    color: '#f59e0b',
  },
  {
    id: 'in_review',
    label: i18n.t('trackers:trackerTemplate.builtinTemplates.inReview'),
    color: '#3b82f6',
  },
  { id: 'done', label: i18n.t('trackers:trackerTemplate.builtinTemplates.done'), color: '#10b981' },
];

const priorityStatus = (): StatusOption[] => [
  { id: 'low', label: i18n.t('trackers:trackerTemplate.builtinTemplates.low'), color: '#94a3b8' },
  {
    id: 'medium',
    label: i18n.t('trackers:trackerTemplate.builtinTemplates.medium'),
    color: '#f59e0b',
  },
  { id: 'high', label: i18n.t('trackers:trackerTemplate.builtinTemplates.high'), color: '#f43f5e' },
];

function layout(
  customColumns: CustomColumnDto[],
  views: TrackerLayoutDto['views'],
): TrackerLayoutDto {
  return { columnOrder: customColumns.map((c) => c.id), customColumns, views };
}

/**
 * A board whose rows are records the workspace already holds. Its columns are
 * the record's own fields, named by the ids the rows endpoint reads them back
 * under, so there is nothing per-board to seed.
 */
function recordLayout(columnOrder: string[], views: TrackerLayoutDto['views']): TrackerLayoutDto {
  return { columnOrder, customColumns: [], views };
}

// Every translated member is a getter so each read resolves against the active
// locale; a plain `i18n.t(...)` here would freeze the English at import time.
// `layout` is one too, because applying a template persists its column and
// status labels as board data: they have to be the language the person was
// working in when they clicked, after which the board owns them.
export const BUILTIN_TRACKER_TEMPLATES: readonly BuiltinTrackerTemplate[] = [
  {
    key: 'content-calendar',
    get displayName() {
      return i18n.t('trackers:trackerTemplate.builtinTemplates.contentCalendar');
    },
    get description() {
      return i18n.t('trackers:trackerTemplate.builtinTemplates.planScheduleAndTrackContent');
    },
    icon: 'calendar-days',
    hue: 'blue',
    itemSource: 'text',
    groupBy: 'status',
    get layout() {
      return layout(
        [
          statusColumn(
            'status',
            i18n.t('trackers:trackerTemplate.builtinTemplates.status'),
            workflowStatus(),
          ),
          column('channel', i18n.t('trackers:trackerTemplate.builtinTemplates.channel'), 'text'),
          column(
            'publish_date',
            i18n.t('trackers:trackerTemplate.builtinTemplates.publishDate'),
            'date',
          ),
          column('owner', i18n.t('trackers:trackerTemplate.builtinTemplates.owner'), 'people'),
        ],
        ['table', 'kanban', 'timeline'],
      );
    },
  },
  {
    key: 'campaign-tracker',
    get displayName() {
      return i18n.t('trackers:trackerTemplate.builtinTemplates.campaignTracker');
    },
    get description() {
      return i18n.t('trackers:trackerTemplate.builtinTemplates.coordinateEveryDeliverableInA');
    },
    icon: 'megaphone',
    hue: 'purple',
    itemSource: 'text',
    groupBy: 'status',
    get layout() {
      return layout(
        [
          statusColumn(
            'status',
            i18n.t('trackers:trackerTemplate.builtinTemplates.status'),
            workflowStatus(),
          ),
          column('owner', i18n.t('trackers:trackerTemplate.builtinTemplates.owner'), 'people'),
          column('due_date', i18n.t('trackers:trackerTemplate.builtinTemplates.dueDate'), 'date'),
          column('budget', i18n.t('trackers:trackerTemplate.builtinTemplates.budget'), 'number'),
        ],
        ['table', 'kanban', 'dashboard'],
      );
    },
  },
  {
    key: 'bug-tracker',
    get displayName() {
      return i18n.t('trackers:trackerTemplate.builtinTemplates.bugTracker');
    },
    get description() {
      return i18n.t('trackers:trackerTemplate.builtinTemplates.triageAndResolveIssuesWith');
    },
    icon: 'bug',
    hue: 'red',
    itemSource: 'text',
    groupBy: 'status',
    get layout() {
      return layout(
        [
          statusColumn(
            'status',
            i18n.t('trackers:trackerTemplate.builtinTemplates.status'),
            workflowStatus(),
          ),
          statusColumn(
            'priority',
            i18n.t('trackers:trackerTemplate.builtinTemplates.priority'),
            priorityStatus(),
          ),
          column(
            'assignee',
            i18n.t('trackers:trackerTemplate.builtinTemplates.assignee'),
            'people',
          ),
          column('reported', i18n.t('trackers:trackerTemplate.builtinTemplates.reported'), 'date'),
        ],
        ['table', 'kanban'],
      );
    },
  },
  {
    key: 'sprint-board',
    get displayName() {
      return i18n.t('trackers:trackerTemplate.builtinTemplates.sprintBoard');
    },
    get description() {
      return i18n.t('trackers:trackerTemplate.builtinTemplates.runASprintAsA');
    },
    icon: 'square-kanban',
    hue: 'green',
    itemSource: 'text',
    groupBy: 'status',
    get layout() {
      return layout(
        [
          statusColumn(
            'status',
            i18n.t('trackers:trackerTemplate.builtinTemplates.status'),
            workflowStatus(),
          ),
          column(
            'points',
            i18n.t('trackers:trackerTemplate.builtinTemplates.storyPoints'),
            'number',
          ),
          column(
            'assignee',
            i18n.t('trackers:trackerTemplate.builtinTemplates.assignee'),
            'people',
          ),
        ],
        ['kanban', 'table'],
      );
    },
  },
  {
    key: 'projects-board',
    get displayName() {
      return i18n.t('trackers:trackerTemplate.builtinTemplates.projectsBoard');
    },
    get description() {
      return i18n.t('trackers:trackerTemplate.builtinTemplates.everyProjectYouCanOpen');
    },
    icon: 'folder-kanban',
    hue: 'orange',
    itemSource: 'projects',
    groupBy: 'status',
    get layout() {
      return recordLayout(
        ['tags', 'ownerId', 'status', 'deadline', 'assetCount', 'lastActivityAt'],
        ['table', 'kanban'],
      );
    },
  },
  {
    key: 'assets-board',
    get displayName() {
      return i18n.t('trackers:trackerTemplate.builtinTemplates.assetsBoard');
    },
    get description() {
      return i18n.t('trackers:trackerTemplate.builtinTemplates.everyDeliverableAcrossYour');
    },
    icon: 'copy',
    hue: 'teal',
    itemSource: 'assets',
    groupBy: 'approvalStatus',
    get layout() {
      return recordLayout(
        [
          'thumbnailUrl',
          'fileName',
          'projectTitle',
          'uploadedBy',
          'uploadedAt',
          'latestVersionNumber',
          'approvalStatus',
          'unresolvedCommentCount',
        ],
        ['table', 'kanban'],
      );
    },
  },
];
/* eslint-enable i18next/no-literal-string */

export function getBuiltinTemplate(key: string): BuiltinTrackerTemplate | undefined {
  return BUILTIN_TRACKER_TEMPLATES.find((template) => template.key === key);
}
